import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import {
  setCategory,
  setKeyword,
  setApiKey,
  setModel,
  searchLiteratureStart,
  searchLiteratureSuccess,
  searchLiteratureFailure
} from '../store/slices/appSlice'
import CategorySelector from '../components/CategorySelector'
import KeywordInput from '../components/KeywordInput'
import APIConfig from '../components/APIConfig'
import { searchLiterature } from '../services/api'
import type { BiologyCategory, AIModel } from '@shared/types'

export default function HomePage() {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const {
    selectedCategory,
    keyword,
    apiKey,
    selectedModel,
    isSearchingLiterature,
    searchError
  } = useAppSelector((state) => state.app)

  const handleSearch = async () => {
    if (!selectedCategory || !keyword.trim() || !apiKey.trim()) {
      return
    }

    dispatch(searchLiteratureStart())

    try {
      const literatures = await searchLiterature(
        selectedCategory,
        keyword,
        apiKey,
        selectedModel
      )
      dispatch(searchLiteratureSuccess(literatures))
      navigate('/literature')
    } catch (error) {
      dispatch(searchLiteratureFailure(
        error instanceof Error ? error.message : '文献检索失败'
      ))
    }
  }

  const canSearch = selectedCategory && keyword.trim() && apiKey.trim()

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      {/* 标题和介绍 */}
      <div className="text-center space-y-3">
        <h1 className="text-4xl font-bold text-gray-900">
          AI生物竞赛题目生成器
        </h1>
        <p className="text-lg text-gray-600">
          基于科研文献,智能生成国际生物奥林匹克竞赛题目
        </p>
      </div>

      {/* 目录选择 */}
      <div className="card">
        <CategorySelector
          selectedCategory={selectedCategory}
          onSelect={(category: BiologyCategory) => dispatch(setCategory(category))}
        />
      </div>

      {/* 关键词输入 */}
      {selectedCategory && (
        <div className="card">
          <KeywordInput
            selectedCategory={selectedCategory}
            keyword={keyword}
            onChange={(kw) => dispatch(setKeyword(kw))}
          />
        </div>
      )}

      {/* API 配置 */}
      {selectedCategory && keyword && (
        <div className="card">
          <APIConfig
            apiKey={apiKey}
            model={selectedModel}
            onApiKeyChange={(key) => dispatch(setApiKey(key))}
            onModelChange={(model: AIModel) => dispatch(setModel(model))}
          />
        </div>
      )}

      {/* 搜索按钮 */}
      {selectedCategory && keyword && (
        <div className="flex flex-col items-center space-y-4">
          <button
            onClick={handleSearch}
            disabled={!canSearch || isSearchingLiterature}
            className="btn btn-primary px-8 py-3 text-lg"
          >
            {isSearchingLiterature ? '检索中...' : '开始检索文献'}
          </button>

          {searchError && (
            <div className="w-full max-w-md p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{searchError}</p>
            </div>
          )}
        </div>
      )}

      {/* 使用说明 */}
      <div className="card bg-blue-50 border-2 border-blue-200">
        <h3 className="font-semibold text-gray-900 mb-3">使用流程</h3>
        <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
          <li>选择生物学科一级目录</li>
          <li>输入二级或更细分的关键词</li>
          <li>配置 AI 模型和 API 密钥</li>
          <li>点击"开始检索文献"获取相关科研文献</li>
          <li>从文献列表中选择一篇文献</li>
          <li>自动生成 3 道选择题及解析</li>
          <li>导出为 LaTeX 格式</li>
        </ol>
      </div>
    </div>
  )
}
